import { Box, Grid, Typography } from '@mui/material';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { useTranslation } from 'react-i18next';
import { useHits, useInstantSearch } from 'react-instantsearch';
import { AlgoliaHit } from 'instantsearch.js';
import { useMemo } from 'react';
import { SupplierHit } from '../../search/search';
import { mapAlgoliaHitToSupplierCardData } from '../../company/common/supplier-card.mappers';
import i18n from '../../i18n';
import DiscoveryViewSwitch from '../DiscoveryViewSwitch';
import QueryTag from '../../search/filters/QueryTag';
import ClearFiltersButton from '../../search/filters/ClearFiltersButton';
import GeoLocationTags from '../../search/filters/GeoLocationTags';
import DiscoverySkeleton from '../DiscoverySkeleton';
import TrumpeterButterflyImage from '../../assets/images/trumpeter_butterfly.png';
import EmptyPagePlaceholder from '../../common/components/EmptyPagePlaceholder';
import { DEFAULT_MAX_MOV_RANGE } from '../state';
import SupplierCard from '../../company/common/SupplierCard';
import SocialTagFilterTags from './filters/SocialTagFilterTags';
import CompanyMovFilterTag from './filters/CompanyMovFilterTag';
import DiscoveryFilters from './DiscoveryFilters';
import CompanyCategoryFilterTags from './filters/CompanyCategoryFilterTags';
import CompanyDeliveryInMyAreaFilterTag from './filters/CompanyDeliverInMyAreaFilterTag';

export default function DiscoveryCompanyHits(): React.JSX.Element {
  const { t } = useTranslation(['discovery', 'common']);
  const { items, results } = useHits<AlgoliaHit<SupplierHit>>();
  const format = new Intl.NumberFormat(i18n.resolvedLanguage);
  const { status } = useInstantSearch();

  const mainComponent = useMemo(() => {
    if (status === 'loading' || !results?.processingTimeMS) {
      return <DiscoverySkeleton />;
    }

    return items.length
      ? (
        <Grid container rowSpacing={2} columnSpacing={2} sx={{ pt: 1.5 }}>
          {items.map((hit) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2.4 }} key={hit.objectID}>
              <SupplierCard displayWishlistButton supplier={mapAlgoliaHitToSupplierCardData(hit)} />
            </Grid>
          ))}
        </Grid>
      )
      : (
        <EmptyPagePlaceholder
          imageSrc={TrumpeterButterflyImage}
          title={t('common:no_result')}
        />
      );
  }, [items, status, results]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <>
        <Box
          sx={{
            position: 'sticky',
            top: 80,
            backgroundColor: 'surface.main',
            zIndex: 7,
            pt: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column-reverse',
                lg: 'row',
              },
              alignItems: 'center',
              gap: {
                xs: 1.5,
                lg: 2,
              },
              mb: 1,
            }}
          >
            <DiscoveryFilters
              fullTextSearchPlaceholder={t('discovery:search_by_name')}
              hideLabelsFilter
              index={import.meta.env.REACT_APP_ALGOLIA_COMPANY_INDEX}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: {
                  lg: 'end',
                  xs: 'space-between',
                },
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: {
                    lg: 'none',
                  },
                }}
              >
                <DiscoveryViewSwitch />
              </Box>
              <Typography variant="bodyMedium" sx={{ color: 'onSurfaceVariant.main' }}>
                {t('discovery:N_supplier', { count: results!.nbHits, formattedCount: format.format(results!.nbHits) })}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              '&:not(:empty)': {
                py: 1,
              },
            }}
          >
            <QueryTag />
            <CompanyMovFilterTag />
            <CompanyDeliveryInMyAreaFilterTag />
            <CompanyCategoryFilterTags />
            <SocialTagFilterTags attribute="social_tags_lvl1" icon={<TrendingUpOutlinedIcon />} />
            <SocialTagFilterTags attribute="social_tags_lvl2" icon={<WorkspacePremiumOutlinedIcon />} />
            <GeoLocationTags />
            <ClearFiltersButton
              sx={{ height: 36 }}
              attributesToCheck={['administrative_areas', 'social_tags_lvl1', 'social_tags_lvl2', 'max_mov', 'is_wholesaler']}
              excludedAttributes={['product_families']}
              defaultRefinements={[0, DEFAULT_MAX_MOV_RANGE]}
              searchMode="COMPANY"
            />
          </Box>
        </Box>
        {mainComponent}
      </>
    </Box>
  );
}